import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { motion, animate, MotionConfig, useMotionValue, useMotionTemplate } from 'framer-motion';
import { FeaturedName } from '../about/FeaturedName';

const timeline = [
    { company: 'Walmart', period: 'Jun 2025', duration: 'Current', current: true, start: new Date(2025, 5, 9) },
    { company: 'DP World', period: 'Nov 2024', duration: '6 months', value: 6, decimals: 0, unit: 'months' },
    { company: 'OpenBet', period: 'Jun 2022', duration: '2.5 years', value: 2.5, decimals: 1, unit: 'years' },
    { company: 'Liltriangle', period: 'Apr 2019', duration: '3.2 years', value: 3.2, decimals: 1, unit: 'years' },
];

// Accent used for the timeline highlights
const accent = '#f0abfc';

// Counts a number up from 0 when it appears
const CountUp = ({ to, decimals = 0, delay = 0 }) => {
    const [val, setVal] = useState(0);
    useEffect(() => {
        const controls = animate(0, to, {
            duration: 1.6,
            delay,
            ease: 'easeOut',
            onUpdate: (v) => setVal(v),
        });
        return () => controls.stop();
    }, [to, delay]);
    return <>{val.toFixed(decimals)}</>;
};

const skills = [
    { title: 'iOS Development', desc: 'Robust, user-friendly applications' },
    { title: 'Xcode', desc: "Apple's integrated development environment" },
    { title: 'UIKit', desc: 'Building and customizing iOS interfaces' },
    { title: 'Swift', desc: 'Efficient, maintainable code' },
    { title: 'SwiftUI', desc: 'Modern, declarative UI design' },
    { title: 'macOS Development', desc: 'Robust, user-friendly desktop applications' },
];

// Theme-agnostic soft colours derived from the current text colour
const soft = (pct) => `color-mix(in srgb, currentColor ${pct}%, transparent)`;

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

const Label = ({ children }) => (
    <p className="mb-3 text-xs font-medium uppercase tracking-[0.25em] opacity-50">{children}</p>
);

// Thin light that travels around the edge of a pill (border-only via a mask)
const AnimatedBorder = ({ delay = 0 }) => {
    const angle = useMotionValue(0);
    useEffect(() => {
        const controls = animate(angle, 360, { duration: 4, ease: 'linear', repeat: Infinity, delay });
        return () => controls.stop();
    }, [angle, delay]);
    const ring = useMotionTemplate`conic-gradient(from ${angle}deg, transparent 0deg, transparent 240deg, ${accent} 330deg, transparent 360deg)`;

    return (
        <motion.span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
                background: ring,
                padding: 1,
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                mask: 'linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)',
            }}
        />
    );
};

// Years and months since a start date, counting the current calendar month
// (Jun 2025 -> Oct 2026 = 17 months = 1y 5m). Always up to date.
const getTenure = (start, now = new Date()) => {
    const total =
        (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()) + 1;
    return { years: Math.floor(total / 12), months: total % 12 };
};

// e.g. "1.5 years", rounded to the nearest half year, counting up from 0
const Tenure = ({ start, delay = 0 }) => {
    const { years, months } = getTenure(start);
    const value = Math.round(((years * 12 + months) / 12) * 2) / 2;
    return (
        <>
            <CountUp to={value} decimals={Number.isInteger(value) ? 0 : 1} delay={delay} />{' '}
            {value === 1 ? 'year' : 'years'}
        </>
    );
};

// Skill card with a soft spotlight that follows the cursor
const SkillCard = ({ skill, i }) => {
    const x = useMotionValue(-200);
    const y = useMotionValue(-200);
    const glow = useMotionTemplate`radial-gradient(140px circle at ${x}px ${y}px, ${soft(14)}, transparent 70%)`;

    return (
        <motion.li
            {...fadeUp(0.3 + i * 0.07)}
            whileHover={{ y: -2, transition: { duration: 0.25 } }}
            onPointerMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                x.set(e.clientX - r.left);
                y.set(e.clientY - r.top);
            }}
            className="group relative overflow-hidden rounded-xl border border-[color:color-mix(in_srgb,currentColor_18%,transparent)] px-4 py-2.5 transition-colors duration-300 hover:border-[color:color-mix(in_srgb,currentColor_45%,transparent)]"
        >
            <motion.span
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{ background: glow }}
            />
            <p className="relative text-sm font-semibold tracking-wide">{skill.title}</p>
            <p className="relative mt-1 text-xs font-light leading-snug opacity-60">{skill.desc}</p>
        </motion.li>
    );
};

const About = () => {
    const { theme, themes } = useContext(ThemeContext);

    // Very soft page-wide spotlight that follows the cursor
    const mx = useMotionValue(-600);
    const my = useMotionValue(-600);
    const spotlight = useMotionTemplate`radial-gradient(480px circle at ${mx}px ${my}px, ${soft(7)}, transparent 70%)`;

    return (
        // Respects the visitor's "reduce motion" setting
        <MotionConfig reducedMotion="user">
        <section
            className={`${themes[theme].text} relative px-6 py-12 sm:px-8 lg:py-10`}
            onPointerMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                mx.set(e.clientX - r.left);
                my.set(e.clientY - r.top);
            }}
        >
            <motion.div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: spotlight }} />
            <div className="relative mx-auto grid w-full max-w-6xl items-center gap-14 text-center lg:grid-cols-[1.35fr_1fr] lg:gap-20">

                {/* Hairline divider between the two columns (desktop only) */}
                <motion.span
                    aria-hidden
                    className="pointer-events-none absolute inset-y-[8%] hidden w-px origin-top lg:block"
                    style={{
                        left: 'calc((100% - 5rem) * 1.35 / 2.35 + 2.5rem)',
                        background: `linear-gradient(to bottom, transparent, ${soft(25)}, transparent)`,
                    }}
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                />

                {/* Career Timeline (first on mobile, left on desktop) */}
                <motion.div {...fadeUp(0.2)} className="order-1 lg:order-2">
                    <Label>Experience</Label>
                    <h2 className="mb-10 text-3xl font-light tracking-tight">
                        Career <span className="font-semibold">Timeline</span>
                    </h2>

                    <div className="flex justify-center">
                    <ol className="relative ml-2 border-l border-current/20 text-left">
                        {/* Accent line that draws itself down */}
                        <motion.span
                            aria-hidden
                            className="pointer-events-none absolute -left-[1px] top-0 h-full w-px origin-top"
                            style={{
                                background: `linear-gradient(to bottom, ${accent}, ${accent}00)`,
                            }}
                            initial={{ scaleY: 0 }}
                            animate={{ scaleY: 1 }}
                            transition={{ duration: 1.3, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        />
                        {/* Glowing light that travels down the line */}
                        <motion.span
                            aria-hidden
                            className="pointer-events-none absolute -left-[2px] h-20 w-[3px] rounded-full bg-gradient-to-b from-transparent via-current to-transparent blur-[1px]"
                            initial={{ top: '-15%', opacity: 0 }}
                            animate={{ top: ['-15%', '100%'], opacity: [0, 0.9, 0.9, 0] }}
                            transition={{ duration: 3.5, delay: 1.5, repeat: Infinity, repeatDelay: 1.5, ease: 'easeInOut' }}
                        />
                        {timeline.map((item, i) => (
                            <motion.li
                                key={item.company}
                                {...fadeUp(0.5 + i * 0.2)}
                                className="relative pb-10 pl-8 last:pb-0"
                            >
                                {/* Pulse ring on the current role */}
                                {i === 0 && (
                                    <motion.span
                                        aria-hidden
                                        className="absolute -left-[5px] top-2 h-2.5 w-2.5 rounded-full bg-current"
                                        initial={{ scale: 1, opacity: 0 }}
                                        animate={{ scale: [1, 3], opacity: [0.5, 0] }}
                                        transition={{ duration: 1.8, delay: 1.2, repeat: Infinity, ease: 'easeOut' }}
                                    />
                                )}
                                <motion.span
                                    className={`absolute -left-[5px] top-2 h-2.5 w-2.5 rounded-full ring-4 ring-current/10 ${
                                        i === 0 ? 'bg-current' : 'bg-current opacity-40'
                                    }`}
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 300, damping: 14, delay: 0.65 + i * 0.2 }}
                                />
                                {/* Company name: clean fade and slide in */}
                                {item.current ? (
                                    <FeaturedName delay={0.75 + i * 0.2}>{item.company}</FeaturedName>
                                ) : (
                                    <motion.p
                                        className="text-lg font-bold leading-tight tracking-tight"
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.7, delay: 0.75 + i * 0.2, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        {item.company}
                                    </motion.p>
                                )}
                                <p className="mt-1 text-sm font-light">
                                    <span className="opacity-60">{item.period}</span>
                                    {item.current && (
                                        <>
                                            <span className="mx-2 inline-block h-1.5 w-1.5 rounded-full bg-current align-middle" />
                                            <span className="font-medium opacity-90">Current</span>
                                        </>
                                    )}
                                </p>
                                {/* Duration badge: subtle tinted pill with count-up */}
                                <motion.span
                                    className="relative mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-xs font-medium tracking-wide"
                                    style={{
                                        background: soft(8),
                                        border: `1px solid ${soft(28)}`,
                                    }}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 1.0 + i * 0.2, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {item.current && <AnimatedBorder delay={1.4 + i * 0.2} />}
                                    <span className="tabular-nums">
                                        {item.current ? (
                                            <Tenure start={item.start} delay={1.1 + i * 0.2} />
                                        ) : (
                                            <>
                                                <CountUp to={item.value} decimals={item.decimals} delay={1.1 + i * 0.2} /> {item.unit}
                                            </>
                                        )}
                                    </span>
                                </motion.span>
                            </motion.li>
                        ))}
                    </ol>
                    </div>
                </motion.div>

                {/* About Me (second on mobile, right on desktop) */}
                <div className="order-2 lg:order-1 lg:text-left">
                    <motion.div {...fadeUp(0.05)}>
                        <Label>Introduction</Label>
                        <h1 className="mb-4 text-4xl font-light tracking-tight">
                            About <span className="font-semibold">Me</span>
                        </h1>
                    </motion.div>

                    <motion.p {...fadeUp(0.15)} className="mx-auto max-w-2xl lg:mr-auto lg:ml-0 text-base font-light leading-relaxed opacity-80">
                        I am a dedicated software engineer with over 7 years of experience in iOS
                        development, delivering high-quality applications that meet user needs and
                        business objectives. I stay current with industry trends and keep refining my
                        craft to bring innovative solutions to life.
                    </motion.p>

                    <motion.h2 {...fadeUp(0.25)} className="mb-3 mt-6 text-xs font-medium uppercase tracking-[0.25em] opacity-50">
                        Expertise
                    </motion.h2>
                    <ul className="mx-auto grid max-w-2xl lg:mr-auto lg:ml-0 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {skills.map((skill, i) => (
                            <SkillCard key={skill.title} skill={skill} i={i} />
                        ))}
                    </ul>

                    <motion.div {...fadeUp(0.75)} className="mx-auto mt-6 max-w-2xl lg:mr-auto lg:ml-0">
                        <div className="mx-auto mb-3 h-px w-12 lg:mr-auto lg:ml-0" style={{ background: soft(30) }} />
                        <Label>Hobby</Label>
                        <p className="text-sm font-light leading-relaxed opacity-80">
                            Building robust websites and apps like this one, dividing responsibilities
                            into modules so each is perfectly optimised for its purpose.
                        </p>
                    </motion.div>
                </div>

            </div>
        </section>
        </MotionConfig>
    );
};

export default About;
